import { useMemo } from "react";
import { useAppDispatch, useAppSelector, useLessonSettings } from "hooks/redux";
import { useRole } from "hooks/common";
import { Space } from "components/Common/Space";
import { TestCheckmark } from "components/Interactive/TestCheckmark";
import { ExerciseCondition } from "components/Typography/ExerciseCondition";
import { TTestExercise, TTestExerciseQuestionItem } from "types/app/exercises";
import { exerciseSelector, setExerciseState } from "store/features/exercises";

import "./TestQuestionItem.styles.scss";

type TTestQuestionItemProps = {
  /**
   * The test exercise question data.
   */
  question: TTestExerciseQuestionItem;
  /**
   * The exercise object to update.
   */
  exercise: TTestExercise;
  /**
   * Whether it’s a homework exercise.
   */
  isHomeworkExercise: boolean;
  /**
   * Function to update the exercise.
   */
  onUpdateExercise: (updatedExercise: TTestExercise) => void;
  /**
   * The question sequence order.
   * @default 1
   */
  order?: number;
};

export const TestQuestionItem: React.FC<TTestQuestionItemProps> = ({
  question,
  exercise,
  onUpdateExercise,
  order = 1,
}) => {
  const { exercises: localExercises } = useAppSelector(exerciseSelector);
  const dispatch = useAppDispatch();

  const { isExerciseReadOnlyMode, isExerciseLiveMode, isExercisePreviewMode } =
    useLessonSettings();
  const { isUserTeacher, isUserAdmin } = useRole();

  const maxAttempts = useMemo(
    () => question.answers.filter((a) => a.isCorrect).length,
    [question]
  );
  const isQuestionDisabled = useMemo(() => {
    const alreadySelected = question.answers.filter(
      (a) => a.isUserSelect
    ).length;
    return alreadySelected >= maxAttempts;
  }, [maxAttempts, question.answers]);

  const handleAnswerChange = (checked: boolean, optionId: string): void => {
    if (isExerciseReadOnlyMode) return;

    const updatedQuestions = exercise.payload.data.questions.map((q) =>
      q.id === question.id
        ? {
            ...q,
            answers: q.answers.map((a) =>
              a.id === optionId ? { ...a, isUserSelect: checked } : a
            ),
          }
        : q
    );

    const updatedExercise = {
      ...exercise,
      payload: {
        ...exercise.payload,
        data: {
          ...exercise.payload.data,
          questions: updatedQuestions,
        },
      },
    };

    const updatedLocalExercises = localExercises.map((ex) =>
      ex.id === updatedExercise.id ? updatedExercise : ex
    );

    dispatch(setExerciseState(updatedLocalExercises));
    onUpdateExercise(updatedExercise);
  };

  return (
    <Space direction="vertical" justify="start" size="small" fullWidth>
      <ExerciseCondition prefix={`${order}.`} text={question.question} />
      {question.answers.map(({ content, id, isCorrect, isUserSelect }) => (
        <TestCheckmark
          key={id}
          label={content}
          name={id.toString()}
          id={id.toString()}
          value={isExercisePreviewMode ? isCorrect : Boolean(isUserSelect)}
          disabled={
            isExercisePreviewMode ||
            Boolean(isUserSelect) ||
            isQuestionDisabled ||
            isUserTeacher
          }
          onChange={(checked) => handleAnswerChange(checked, id.toString())}
          isCorrect={isCorrect}
        />
      ))}
    </Space>
  );
};
